<template>
  <div
    ref="rootEl"
    class="hd"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <!-- Sticky page header -->
    <header class="hd-topbar">
      <div class="hd-topbar__inner">
        <div class="hd-topbar__left">
          <button type="button" class="hd-icon-btn" aria-label="Go back" @click="goBack">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="hd-topbar__titles">
            <h1 class="hd-topbar__h1">{{ pageHeading }}</h1>
            <p class="hd-topbar__sub">{{ pageSubheading }}</p>
          </div>
        </div>
        <div class="hd-topbar__actions">
          <button
            type="button"
            class="hd-icon-btn"
            :disabled="isRefreshing || isLoadingDashboard"
            aria-label="Refresh dashboard"
            @click="refreshData"
            :title="isRefreshing ? 'Refreshing…' : 'Refresh'"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true" :class="{ 'hd-spin': isRefreshing }">
              <path d="M1 4V10H7M23 20V14H17" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M3.51 15A9 9 0 0 0 18.36 18.36L23 14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button type="button" class="hd-icon-btn" aria-label="Export dashboard JSON" @click="exportData" title="Export JSON">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M21 15V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V15" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
      <div v-if="pullProgress > 0" class="hd-pull-pill" role="status" aria-live="polite">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" :class="{ 'hd-spin': pullProgress >= PULL_THRESHOLD }">
          <path d="M1 4V10H7M23 20V14H17" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M3.51 15A9 9 0 0 0 18.36 18.36L23 14" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ pullProgress >= PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh' }}
      </div>
    </header>

    <main class="hd-main">

      <!-- ── SKELETON ────────────────────────────── -->
      <template v-if="isLoadingDashboard && normalized === null">
        <div class="hd-kpi-grid">
          <div v-for="i in 4" :key="'sk'+i" class="hd-sk hd-sk--kpi" />
        </div>
        <div class="hd-sk hd-sk--finance" />
        <div class="hd-2col">
          <div class="hd-sk hd-sk--panel" />
          <div class="hd-sk hd-sk--panel" />
        </div>
        <div class="hd-sk hd-sk--chart" />
      </template>

      <!-- ── FULL ERROR ─────────────────────────── -->
      <div v-else-if="dashboardError && normalized === null" class="hd-fullstate hd-fullstate--error" role="alert">
        <div class="hd-fullstate__icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h2 class="hd-fullstate__h">Could not load dashboard</h2>
        <p class="hd-fullstate__p">{{ dashboardError }}</p>
        <button type="button" class="hd-cta" @click="refreshData">Try again</button>
      </div>

      <!-- ── CONTENT ────────────────────────────── -->
      <template v-else-if="normalized">

        <!-- inline refresh error -->
        <div v-if="dashboardError" class="hd-toast hd-toast--err" role="alert">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          {{ dashboardError }}
          <button type="button" class="hd-toast__retry" @click="refreshData">Retry</button>
        </div>

        <!-- ── KPI CARDS ──────────────────────── -->
        <section class="hd-kpi-grid" aria-label="Key performance indicators">

          <!-- Active guests -->
          <article class="hd-kpi hd-kpi--guests">
            <div class="hd-kpi__icon-wrap">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div class="hd-kpi__body">
              <span class="hd-kpi__label">Active guests</span>
              <span class="hd-kpi__value">{{ formatInt(normalized.guests.active) }}</span>
              <span class="hd-kpi__hint">Currently on property</span>
            </div>
          </article>

          <!-- Onboarded -->
          <article class="hd-kpi hd-kpi--onboard">
            <div class="hd-kpi__icon-wrap">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
                <path d="M20 8v6M23 11l-3 3-3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="hd-kpi__body">
              <span class="hd-kpi__label">Onboarded this month</span>
              <span class="hd-kpi__value">{{ formatInt(normalized.guests.onboardedThisMonth) }}</span>
              <span class="hd-kpi__hint">New check-ins (calendar month)</span>
            </div>
          </article>

          <!-- Rent -->
          <article class="hd-kpi hd-kpi--rent">
            <div class="hd-kpi__icon-wrap">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2"/>
                <path d="M2 10h20" stroke="currentColor" stroke-width="2"/>
              </svg>
            </div>
            <div class="hd-kpi__body">
              <span class="hd-kpi__label">Rent this month</span>
              <span class="hd-kpi__value hd-kpi__value--sm">{{ formatNpr(rentThisMonthNumber) }}</span>
              <span class="hd-kpi__hint">Rent payments only</span>
            </div>
          </article>

          <!-- Occupancy -->
          <article class="hd-kpi hd-kpi--occ">
            <div class="hd-kpi__icon-wrap">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="hd-kpi__body">
              <span class="hd-kpi__label">
                Guest occupancy rate
                <span class="hd-tooltip-anchor" tabindex="0" :title="occupancyTooltipText" :aria-label="occupancyTooltipText" role="img">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 16v-4M12 8h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
                </span>
              </span>
              <span class="hd-kpi__value">{{ occupancyDisplayPercent }}</span>
              <div class="hd-occ-bar">
                <div class="hd-occ-bar__fill" :style="{ width: occupancyDisplayPercent }" />
              </div>
            </div>
          </article>

        </section>

        <!-- ── FINANCE ─────────────────────────── -->
        <section v-if="normalized.financeThisMonth" class="hd-finance" aria-label="Finance this period">
          <div class="hd-finance__left">
            <div class="hd-finance__eyebrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              Finance snapshot
            </div>
            <p class="hd-finance__period">{{ financeRangeLabel }}</p>
            <div class="hd-finance__balance" :class="balanceToneClass">
              <span class="hd-finance__balance-lbl">Net balance</span>
              <span class="hd-finance__balance-val">{{ formatNpr(normalized.financeThisMonth.balance) }}</span>
            </div>
            <div class="hd-finance__spend-bar" v-if="normalized.financeThisMonth.income > 0">
              <div class="hd-finance__spend-fill" :style="{ width: expenseRatioPct + '%' }" />
            </div>
            <p v-if="normalized.financeThisMonth.income > 0" class="hd-finance__spend-label">
              {{ expenseRatioPct }}% of income spent
            </p>
          </div>
          <div class="hd-finance__right">
            <div class="hd-finance__stat hd-finance__stat--income">
              <div class="hd-finance__stat-dot" />
              <div>
                <div class="hd-finance__stat-lbl">Income</div>
                <div class="hd-finance__stat-val">{{ formatNpr(normalized.financeThisMonth.income) }}</div>
              </div>
            </div>
            <div class="hd-finance__stat hd-finance__stat--expense">
              <div class="hd-finance__stat-dot" />
              <div>
                <div class="hd-finance__stat-lbl">Expense</div>
                <div class="hd-finance__stat-val">{{ formatNpr(normalized.financeThisMonth.expense) }}</div>
              </div>
            </div>
            <div class="hd-finance__stat hd-finance__stat--rent">
              <div class="hd-finance__stat-dot" />
              <div>
                <div class="hd-finance__stat-lbl">Rent income</div>
                <div class="hd-finance__stat-val">{{ formatNpr(normalized.financeThisMonth.rentIncome) }}</div>
              </div>
            </div>
          </div>
        </section>

        <div v-else class="hd-notice" aria-live="polite">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          Finance data is unavailable for this period.
        </div>

        <!-- ── BEDS + SPACES ───────────────────── -->
        <div class="hd-2col hd-2col--beds">

          <!-- Bed status -->
          <section class="hd-panel" aria-label="Bed capacity">
            <div class="hd-panel__head">
              <div class="hd-panel__icon hd-panel__icon--beds">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M2 12h20M2 20V8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 12V8M18 12V8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              </div>
              <div>
                <h2 class="hd-panel__title">Bed status</h2>
                <p class="hd-panel__sub">{{ bedTotal }} total beds</p>
              </div>
            </div>
            <div v-if="bedEntries.length === 0" class="hd-empty">No bed status data.</div>
            <div v-else class="hd-beds-body">
              <!-- Donut -->
              <div class="hd-donut-wrap" aria-hidden="true">
                <div class="hd-donut" :style="{ background: conicGradient }" />
                <div class="hd-donut-inner">
                  <span class="hd-donut-inner__num">{{ bedTotal }}</span>
                  <span class="hd-donut-inner__lbl">beds</span>
                </div>
              </div>
              <!-- Legend rows -->
              <div class="hd-bed-legend">
                <div v-for="(e, idx) in bedEntries" :key="e.key" class="hd-bed-legend__row">
                  <span class="hd-bed-legend__dot" :style="{ background: colorForStatusKey(e.key, idx) }" />
                  <span class="hd-bed-legend__name">{{ e.label }}</span>
                  <div class="hd-bed-legend__bar-wrap">
                    <div class="hd-bed-legend__bar" :style="{ width: bedPct(e.count) + '%', background: colorForStatusKey(e.key, idx) }" />
                  </div>
                  <span class="hd-bed-legend__val">{{ e.count }}</span>
                  <span class="hd-bed-legend__pct">{{ bedPct(e.count) }}%</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Spaces -->
          <section class="hd-panel" aria-label="Room types">
            <div class="hd-panel__head">
              <div class="hd-panel__icon hd-panel__icon--rooms">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <div>
                <h2 class="hd-panel__title">By space type</h2>
                <p class="hd-panel__sub">Beds &amp; status per room category</p>
              </div>
            </div>
            <div v-if="!normalized.spaces.length" class="hd-empty">No space rows.</div>
            <div v-else class="hd-space-list">
              <div
                v-for="(space, sIdx) in normalized.spaces"
                :key="space.objectSpaceId || space.spaceType + '-' + sIdx"
                class="hd-space-card"
              >
                <div class="hd-space-card__top">
                  <div class="hd-space-card__badge">{{ roomTypeLabel(space.spaceType) }}</div>
                  <span class="hd-space-card__beds">{{ space.bedsPlanned != null ? formatInt(space.bedsPlanned) : '—' }} beds</span>
                </div>
                <!-- Status chips -->
                <div class="hd-space-card__chips">
                  <span
                    v-for="(chip, cIdx) in spaceStatusChips(space)"
                    :key="chip.key"
                    class="hd-chip2"
                    :style="{ '--chip-c': colorForStatusKey(chip.key, cIdx) }"
                  >{{ chip.label }}: <strong>{{ chip.count }}</strong></span>
                </div>
                <!-- Horizontal bar -->
                <div class="hd-space-hbar" role="img" :aria-label="miniBarLabel(space)">
                  <div
                    v-for="(seg, sI) in spaceSegments(space)"
                    :key="seg.key"
                    class="hd-space-hbar__seg"
                    :style="{ width: seg.pct + '%', background: colorForStatusKey(seg.key, sI) }"
                  />
                </div>
              </div>
            </div>
          </section>

        </div>

        <!-- ── WEEKLY CHART ────────────────────── -->
        <section class="hd-panel hd-panel--chart" aria-label="Weekly trend">
          <div class="hd-panel__head">
            <div class="hd-panel__icon hd-panel__icon--chart">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
            <div>
              <h2 class="hd-panel__title">Weekly trend</h2>
              <p class="hd-panel__sub">Onboardings &amp; rent by ISO week (YYYY-Www)</p>
            </div>
            <div class="hd-chart-legend">
              <span class="hd-chart-legend__item"><i class="hd-chart-legend__bar" />Onboardings</span>
              <span class="hd-chart-legend__item"><i class="hd-chart-legend__line" />Rent</span>
            </div>
          </div>
          <div v-if="!normalized.weeklyGraph.length" class="hd-empty">No weekly data yet.</div>
          <div v-else class="hd-chart-wrap">
            <div class="hd-chart-scroll">
              <svg
                class="hd-chart-svg"
                :viewBox="'0 0 ' + chartWidth + ' ' + chartHeight"
                :width="chartWidth"
                :height="chartHeight"
                aria-hidden="true"
              >
                <!-- Horizontal grid lines -->
                <line v-for="y in [0.25, 0.5, 0.75, 1]" :key="'hg'+y"
                  class="hd-grid-h"
                  x1="0"
                  :y1="gridY(y)"
                  :x2="chartWidth"
                  :y2="gridY(y)"
                />
                <!-- Bar gradient defs -->
                <defs>
                  <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#7c3aed" />
                    <stop offset="100%" stop-color="#5b2e8e" stop-opacity="0.75" />
                  </linearGradient>
                  <linearGradient id="barGradHov" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#a78bfa" />
                    <stop offset="100%" stop-color="#7c3aed" />
                  </linearGradient>
                </defs>
                <!-- Bars -->
                <rect
                  v-for="(g, i) in normalized.weeklyGraph"
                  :key="'bar-' + i"
                  class="hd-bar"
                  :x="barX(i) - barW / 2"
                  :y="chartHeight - 28 - barHeight(g.occupancyCount)"
                  :width="barW"
                  :height="barHeight(g.occupancyCount)"
                  rx="4"
                  fill="url(#barGrad)"
                >
                  <title>{{ comboTooltip(i) }}</title>
                </rect>
                <!-- Revenue line -->
                <polyline
                  class="hd-rev-line"
                  fill="none"
                  stroke="#0ea5e9"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  :points="revenuePolylinePoints"
                />
                <!-- Revenue dots -->
                <circle
                  v-for="(g, i) in normalized.weeklyGraph"
                  :key="'dot-' + i"
                  class="hd-rev-dot"
                  :cx="barX(i)"
                  :cy="revenueY(g.revenue)"
                  r="4"
                  fill="#0ea5e9"
                  stroke="#fff"
                  stroke-width="2"
                >
                  <title>{{ comboTooltip(i) }}</title>
                </circle>
              </svg>
            </div>
            <!-- X-axis labels below SVG -->
            <div class="hd-chart-xlabels" :style="{ width: chartWidth + 'px' }">
              <span
                v-for="(g, i) in normalized.weeklyGraph"
                :key="'xl-' + i"
                class="hd-chart-xlabel"
                :style="{ left: barX(i) - 28 + 'px' }"
              >{{ weekLabel(g.week) }}</span>
            </div>
          </div>
        </section>

        <!-- Debug -->
        <details v-if="normalized.objectid" class="hd-debug">
          <summary class="hd-debug__summary">Debug info</summary>
          <code class="hd-debug__code">Object ID: {{ normalized.objectid }}</code>
        </details>

      </template>
    </main>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { readSessionToken } from '@/utilities/sessionTokenStorage';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';
import {
  normalizeDashboardPayload,
  formatNpr,
  parseNumericString,
  bedStatusEntries,
  bedStatusTotal,
  formatMysqlYearWeekLabel,
  formatFinanceRangeSubtitle,
  colorForStatusKey,
  weeklyGraphMaxima,
} from '@/utilities/dashboardUtils.js';

const REFRESH_DEBOUNCE_MS = 450;
const PULL_THRESHOLD = 72;

export default {
  name: 'ObjectMainPage',
  props: {
    objectData: {
      type: Object,
      default: () => ({}),
    },
    objectSpaces: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      normalized: null,
      rawPayload: null,
      isLoadingDashboard: true,
      isRefreshing: false,
      dashboardError: null,
      lastRefreshAt: 0,
      expandedSpaceKeys: [],
      roomTypeMapping: {
        '1S': '1 Sharing',
        '2S': '2 Sharing',
        '3S': '3 Sharing',
        '4S': '4 Sharing',
        '5S': '5 Sharing',
        '6S': '6 Sharing',
      },
      touchStartY: null,
      pullProgress: 0,
      PULL_THRESHOLD,
    };
  },
  computed: {
    pageHeading() {
      const n = this.objectData?.objectName || this.$route.params?.objectName;
      if (n && String(n).trim()) return String(n).trim();
      return 'Dashboard';
    },
    pageSubheading() {
      const id = this.$route.params?.id;
      if (!id) return 'Hostel overview';
      const tail = id.length > 10 ? `…${id.slice(-8)}` : id;
      return `Property ${tail}`;
    },
    rentThisMonthNumber() {
      if (!this.normalized) return 0;
      return parseNumericString(this.normalized.rentRevenueThisMonth ?? this.normalized.totalRevenue);
    },
    occupancyDisplayPercent() {
      if (!this.normalized) return '0%';
      const v = this.normalized.occupancyRateDisplay;
      const n = Number.isFinite(v) ? v : 0;
      return `${n.toFixed(1)}%`;
    },
    occupancyTooltipText() {
      return (
        'Share of active guests who onboarded this calendar month (provider metric). ' +
        'This is not the same as bed occupancy.'
      );
    },
    financeRangeLabel() {
      const f = this.normalized?.financeThisMonth;
      if (!f) return '';
      const sub = formatFinanceRangeSubtitle(f.fromdate, f.todate);
      return sub || `${f.fromdate} – ${f.todate}`;
    },
    balanceToneClass() {
      const b = this.normalized?.financeThisMonth?.balance ?? 0;
      if (b > 0) return 'hd-finance__balance--pos';
      if (b < 0) return 'hd-finance__balance--neg';
      return 'hd-finance__balance--zero';
    },
    bedEntries() {
      if (!this.normalized) return [];
      return bedStatusEntries(this.normalized.bedStatusTotals);
    },
    bedTotal() {
      if (!this.normalized) return 0;
      return bedStatusTotal(this.normalized.bedStatusTotals);
    },
    conicGradient() {
      const entries = this.bedEntries;
      const total = this.bedTotal || 1;
      let acc = 0;
      const parts = entries.map((e, idx) => {
        const deg = (e.count / total) * 360;
        const start = acc;
        acc += deg;
        const c = colorForStatusKey(e.key, idx);
        return `${c} ${start}deg ${acc}deg`;
      });
      if (!parts.length) return '#e2e8f0';
      return `conic-gradient(${parts.join(', ')})`;
    },
    bedStackDescription() {
      return this.bedEntries.map((e) => `${e.label} ${e.count}`).join(', ');
    },
    chartWidth() {
      return Math.max(360, (this.normalized?.weeklyGraph?.length || 1) * 80);
    },
    chartHeight() {
      return 200;
    },
    barW() {
      const n = this.normalized?.weeklyGraph?.length || 1;
      return Math.min(36, this.chartWidth / n - 10);
    },
    weeklyMax() {
      return weeklyGraphMaxima(this.normalized?.weeklyGraph || []);
    },
    revenuePolylinePoints() {
      const g = this.normalized?.weeklyGraph;
      if (!g?.length) return '';
      return g
        .map((row, i) => `${this.barX(i)},${this.revenueY(row.revenue)}`)
        .join(' ');
    },
    expenseRatioPct() {
      const f = this.normalized?.financeThisMonth;
      if (!f || f.income <= 0) return 0;
      return Math.min(100, Math.round((f.expense / f.income) * 100));
    },
  },
  mounted() {
    this.fetchDashboardData();
  },
  methods: {
    formatNpr,
    formatInt(n) {
      const x = Math.round(Number(n) || 0);
      return x.toLocaleString('en-NP');
    },
    colorForStatusKey,
    weekLabel(w) {
      return formatMysqlYearWeekLabel(w);
    },
    bedPct(count) {
      if (!this.bedTotal) return 0;
      return Math.round((count / this.bedTotal) * 100);
    },
    gridY(frac) {
      const innerH = this.chartHeight - 36;
      return 8 + innerH * (1 - frac);
    },
    roomTypeLabel(spaceType) {
      if (!spaceType) return '—';
      return this.roomTypeMapping[spaceType] || spaceType;
    },
    spaceRowKey(space, idx) {
      return space.objectSpaceId || `${space.spaceType || 'space'}-${idx}`;
    },
    isSpaceExpanded(space, idx) {
      return this.expandedSpaceKeys.includes(this.spaceRowKey(space, idx));
    },
    toggleSpace(key) {
      const i = this.expandedSpaceKeys.indexOf(key);
      if (i >= 0) {
        this.expandedSpaceKeys.splice(i, 1);
      } else {
        this.expandedSpaceKeys.push(key);
      }
    },
    spaceStatusChips(space) {
      const sc = space.statusCounts && typeof space.statusCounts === 'object' ? space.statusCounts : {};
      return bedStatusEntries(sc);
    },
    spaceSegments(space) {
      const sc = space.statusCounts && typeof space.statusCounts === 'object' ? space.statusCounts : {};
      const entries = bedStatusEntries(sc);
      const total = bedStatusTotal(sc) || 1;
      return entries.map((e) => ({
        key: e.key,
        pct: (e.count / total) * 100,
      }));
    },
    miniBarLabel(space) {
      return this.spaceStatusChips(space)
        .map((c) => `${c.label} ${c.count}`)
        .join(', ');
    },
    barX(i) {
      const n = this.normalized?.weeklyGraph?.length || 1;
      const pad = 24;
      const usable = this.chartWidth - pad * 2;
      return pad + (usable * (i + 0.5)) / Math.max(n, 1);
    },
    barHeight(count) {
      const max = this.weeklyMax.maxCount;
      const innerH = this.chartHeight - 36;
      const h = innerH * (count / max);
      return Math.max(count > 0 ? 6 : 0, h);
    },
    revenueY(rev) {
      const max = this.weeklyMax.maxRevenue;
      const innerH = this.chartHeight - 36;
      const y = innerH - (parseNumericString(rev) / max) * innerH;
      return 8 + Math.min(innerH, Math.max(0, y));
    },
    comboTooltip(i) {
      const g = this.normalized.weeklyGraph[i];
      if (!g) return '';
      return `${this.weekLabel(g.week)}: ${g.occupancyCount} onboardings, ${formatNpr(g.revenue)} rent`;
    },
    stackSegStyle(entry, idx) {
      const total = this.bedTotal || 1;
      const pct = (entry.count / total) * 100;
      return {
        width: `${pct}%`,
        background: colorForStatusKey(entry.key, idx),
      };
    },
    async fetchDashboardData() {
      this.isLoadingDashboard = true;
      this.dashboardError = null;
      try {
        if (!readSessionToken()) {
          this.dashboardError = 'Not signed in.';
          return;
        }
        const objectId = this.$route.params.id;
        const response = await apiService.getObjectDashboard(objectId);
        this.rawPayload = response.data;
        this.normalized = normalizeDashboardPayload(response.data || {});
      } catch (error) {
        if (isHmsSubscriptionInactiveError(error)) {
          this.dashboardError = null;
          this.normalized = normalizeDashboardPayload({});
        } else if (isNoDataError(error)) {
          this.normalized = normalizeDashboardPayload({});
        } else {
          this.dashboardError =
            error?.response?.data?.message || error?.message || 'Something went wrong.';
        }
      } finally {
        this.isLoadingDashboard = false;
      }
    },
    async refreshData() {
      const now = Date.now();
      if (now - this.lastRefreshAt < REFRESH_DEBOUNCE_MS && this.lastRefreshAt > 0) {
        return;
      }
      this.lastRefreshAt = now;
      this.isRefreshing = true;
      try {
        await this.fetchDashboardData();
        this.$emit('refresh-data');
      } finally {
        this.isRefreshing = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    exportData() {
      const blob = new Blob([JSON.stringify(this.rawPayload || this.normalized || {}, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hostel-dashboard-${this.$route.params.id || 'export'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onTouchStart(e) {
      const sc = document.scrollingElement;
      if (sc && sc.scrollTop > 4) return;
      this.touchStartY = e.touches[0].clientY;
    },
    onTouchMove(e) {
      if (this.touchStartY == null) return;
      const dy = e.touches[0].clientY - this.touchStartY;
      if (dy > 0) {
        this.pullProgress = Math.min(PULL_THRESHOLD + 20, dy);
        if (this.pullProgress > 20) e.preventDefault();
      }
    },
    onTouchEnd() {
      if (this.pullProgress >= PULL_THRESHOLD) {
        this.refreshData();
      }
      this.touchStartY = null;
      this.pullProgress = 0;
    },
  },
};
</script>

<style scoped>
/* ── Base ───────────────────────────────────────── */
.hd {
  min-height: 100%;
  background: #f4f6fb;
  color: #0f172a;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
}

/* ── Topbar ─────────────────────────────────────── */
.hd-topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #e8edf5;
  box-shadow: 0 1px 0 0 rgba(15, 23, 42, 0.04);
}

.hd-topbar__inner {
  max-width: 1160px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hd-topbar__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.hd-topbar__titles {
  min-width: 0;
}

.hd-topbar__h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.025em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hd-topbar__sub {
  margin: 1px 0 0;
  font-size: 0.78rem;
  color: #64748b;
}

.hd-topbar__actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* Icon button */
.hd-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
}

.hd-icon-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #1e293b;
}

.hd-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.hd-spin {
  animation: hd-spin 0.9s linear infinite;
}

@keyframes hd-spin {
  to { transform: rotate(360deg); }
}

/* Pull-to-refresh pill */
.hd-pull-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px 12px 6px;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6d28d9;
  letter-spacing: 0.02em;
}

/* ── Main layout ────────────────────────────────── */
.hd-main {
  max-width: 1160px;
  margin: 0 auto;
  padding: 20px 20px 40px;
}

/* ── Skeleton ───────────────────────────────────── */
@keyframes hd-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.hd-sk {
  border-radius: 16px;
  background: linear-gradient(90deg, #e9edf4 25%, #f5f7fb 50%, #e9edf4 75%);
  background-size: 400% 100%;
  animation: hd-shimmer 1.4s ease-in-out infinite;
}

.hd-sk--kpi   { height: 108px; }
.hd-sk--finance { height: 148px; margin-bottom: 16px; }
.hd-sk--panel { height: 240px; }
.hd-sk--chart { height: 260px; margin-top: 16px; }

.hd-kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

@media (min-width: 860px) {
  .hd-kpi-grid { grid-template-columns: repeat(4, 1fr); }
}

/* ── Full-page states ───────────────────────────── */
.hd-fullstate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 24px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.hd-fullstate--error {
  border-color: #fecdd3;
  background: #fff1f2;
  color: #9f1239;
}

.hd-fullstate__icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: #ffe4e6;
  margin-bottom: 16px;
}

.hd-fullstate__h {
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 700;
}

.hd-fullstate__p {
  margin: 0 0 20px;
  font-size: 0.9rem;
  color: #be123c;
}

.hd-cta {
  padding: 10px 22px;
  border-radius: 10px;
  border: none;
  background: #5b2e8e;
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background 0.15s;
}

.hd-cta:hover { background: #4a2575; }

/* ── Inline toast ───────────────────────────────── */
.hd-toast {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
}

.hd-toast--err {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
}

.hd-toast__retry {
  margin-left: auto;
  padding: 5px 14px;
  border-radius: 8px;
  border: none;
  background: #5b2e8e;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
}

/* ── KPI cards ──────────────────────────────────── */
.hd-kpi {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 18px;
  padding: 18px 18px 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03);
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
}

.hd-kpi::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  border-radius: 18px 18px 0 0;
}

.hd-kpi--guests::before  { background: linear-gradient(90deg, #8b5cf6, #6d28d9); }
.hd-kpi--onboard::before { background: linear-gradient(90deg, #10b981, #059669); }
.hd-kpi--rent::before    { background: linear-gradient(90deg, #f59e0b, #d97706); }
.hd-kpi--occ::before     { background: linear-gradient(90deg, #0ea5e9, #0284c7); }

.hd-kpi:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.09);
}

.hd-kpi__icon-wrap {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hd-kpi--guests  .hd-kpi__icon-wrap { background: #ede9fe; color: #6d28d9; }
.hd-kpi--onboard .hd-kpi__icon-wrap { background: #d1fae5; color: #059669; }
.hd-kpi--rent    .hd-kpi__icon-wrap { background: #fef3c7; color: #d97706; }
.hd-kpi--occ     .hd-kpi__icon-wrap { background: #e0f2fe; color: #0284c7; }

.hd-kpi__body {
  flex: 1;
  min-width: 0;
}

.hd-kpi__label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.hd-kpi__value {
  display: block;
  margin-top: 6px;
  font-size: 1.65rem;
  font-weight: 800;
  letter-spacing: -0.035em;
  color: #0f172a;
  line-height: 1.1;
}

.hd-kpi__value--sm { font-size: 1.15rem; margin-top: 8px; }

.hd-kpi__hint {
  display: block;
  margin-top: 5px;
  font-size: 0.7rem;
  color: #94a3b8;
  line-height: 1.3;
}

.hd-tooltip-anchor {
  display: inline-flex;
  align-items: center;
  color: #94a3b8;
  cursor: help;
}

.hd-tooltip-anchor:hover { color: #0284c7; }

/* Occupancy mini bar */
.hd-occ-bar {
  margin-top: 8px;
  height: 5px;
  border-radius: 4px;
  background: #e0f2fe;
  overflow: hidden;
}

.hd-occ-bar__fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #0ea5e9, #0284c7);
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  max-width: 100%;
}

/* ── Finance ────────────────────────────────────── */
.hd-finance {
  display: grid;
  gap: 0;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(91, 46, 142, 0.07);
}

@media (min-width: 640px) {
  .hd-finance {
    grid-template-columns: 1fr 1fr;
  }
}

.hd-finance__left {
  padding: 22px 24px;
  background: linear-gradient(145deg, #faf5ff 0%, #f5f3ff 100%);
  border-right: 1px solid #ede9fe;
}

.hd-finance__right {
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
}

.hd-finance__eyebrow {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #7c3aed;
  margin-bottom: 6px;
}

.hd-finance__period {
  margin: 0 0 14px;
  font-size: 0.82rem;
  color: #64748b;
}

.hd-finance__balance {
  margin-bottom: 12px;
}

.hd-finance__balance-lbl {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
}

.hd-finance__balance-val {
  display: block;
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  margin-top: 4px;
  line-height: 1.05;
}

.hd-finance__balance--pos .hd-finance__balance-val { color: #16a34a; }
.hd-finance__balance--neg .hd-finance__balance-val { color: #dc2626; }
.hd-finance__balance--zero .hd-finance__balance-val { color: #334155; }

.hd-finance__spend-bar {
  height: 6px;
  border-radius: 4px;
  background: #ede9fe;
  overflow: hidden;
  margin-bottom: 5px;
}

.hd-finance__spend-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #7c3aed, #a78bfa);
  transition: width 0.6s ease;
  max-width: 100%;
}

.hd-finance__spend-label {
  margin: 0;
  font-size: 0.72rem;
  color: #7c3aed;
  font-weight: 600;
}

.hd-finance__stat {
  display: flex;
  align-items: center;
  gap: 12px;
}

.hd-finance__stat-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.hd-finance__stat--income  .hd-finance__stat-dot { background: #16a34a; }
.hd-finance__stat--expense .hd-finance__stat-dot { background: #dc2626; }
.hd-finance__stat--rent    .hd-finance__stat-dot { background: #7c3aed; }

.hd-finance__stat-lbl {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}

.hd-finance__stat-val {
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

/* Finance unavailable notice */
.hd-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 20px;
}

/* ── 2-column layout ────────────────────────────── */
.hd-2col {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;
}

@media (min-width: 760px) {
  .hd-2col { grid-template-columns: 1fr 1fr; }
}

/* ── Panels (generic card) ──────────────────────── */
.hd-panel {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 20px;
  padding: 20px 20px 18px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
}

.hd-panel--chart { margin-top: 0; }

.hd-panel__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}

.hd-panel__icon {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hd-panel__icon--beds  { background: #ede9fe; color: #6d28d9; }
.hd-panel__icon--rooms { background: #d1fae5; color: #059669; }
.hd-panel__icon--chart { background: #e0f2fe; color: #0284c7; }

.hd-panel__title {
  margin: 0 0 2px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.hd-panel__sub {
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
}

.hd-empty {
  padding: 20px 0;
  font-size: 0.875rem;
  color: #94a3b8;
  text-align: center;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
}

/* ── Beds body ──────────────────────────────────── */
.hd-beds-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

@media (min-width: 480px) {
  .hd-beds-body {
    flex-direction: row;
    align-items: flex-start;
  }
}

/* Donut */
.hd-donut-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  margin: 0 auto;
}

.hd-donut {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  box-shadow: 0 0 0 4px #fff, 0 2px 10px rgba(15, 23, 42, 0.1);
}

.hd-donut-inner {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.hd-donut-inner::before {
  content: '';
  position: absolute;
  inset: 20%;
  background: #fff;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px #f0f4f8;
}

.hd-donut-inner__num {
  position: relative;
  font-size: 1.3rem;
  font-weight: 900;
  color: #0f172a;
  line-height: 1;
}

.hd-donut-inner__lbl {
  position: relative;
  font-size: 0.6rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* Legend rows */
.hd-bed-legend {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hd-bed-legend__row {
  display: grid;
  grid-template-columns: 10px 1fr 80px 28px 36px;
  gap: 6px;
  align-items: center;
  font-size: 0.82rem;
}

.hd-bed-legend__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.hd-bed-legend__name {
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hd-bed-legend__bar-wrap {
  height: 6px;
  border-radius: 4px;
  background: #f1f5f9;
  overflow: hidden;
}

.hd-bed-legend__bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
  max-width: 100%;
}

.hd-bed-legend__val {
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: #0f172a;
}

.hd-bed-legend__pct {
  font-size: 0.7rem;
  color: #64748b;
  text-align: right;
}

/* ── Space cards ────────────────────────────────── */
.hd-space-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hd-space-card {
  border: 1px solid #f0f4f8;
  border-radius: 14px;
  padding: 14px 16px 12px;
  background: #fafbfd;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.hd-space-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.hd-space-card__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.hd-space-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 8px;
  background: #ede9fe;
  color: #5b21b6;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.hd-space-card__beds {
  font-size: 0.78rem;
  font-weight: 700;
  color: #64748b;
}

.hd-space-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}

.hd-chip2 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #374151;
  border: 1.5px solid color-mix(in srgb, var(--chip-c) 25%, transparent);
  background: color-mix(in srgb, var(--chip-c) 10%, white);
}

.hd-chip2 strong { font-weight: 800; color: var(--chip-c); }

.hd-space-hbar {
  height: 7px;
  border-radius: 5px;
  overflow: hidden;
  background: #e9edf4;
  display: flex;
}

.hd-space-hbar__seg {
  min-width: 2px;
  height: 100%;
  transition: width 0.4s ease;
}

/* ── Chart ──────────────────────────────────────── */
.hd-panel__head .hd-chart-legend {
  margin-left: auto;
  margin-top: 0;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.hd-chart-legend {
  display: flex;
  gap: 14px;
  font-size: 0.75rem;
  color: #64748b;
}

.hd-chart-legend__item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.hd-chart-legend__bar {
  display: inline-block;
  width: 12px;
  height: 10px;
  border-radius: 3px;
  background: linear-gradient(180deg, #7c3aed, #5b2e8e);
}

.hd-chart-legend__line {
  display: inline-block;
  width: 18px;
  height: 3px;
  border-radius: 2px;
  background: #0ea5e9;
  position: relative;
  top: 0px;
}

.hd-chart-wrap {
  position: relative;
  overflow: hidden;
}

.hd-chart-scroll {
  overflow-x: auto;
  padding-bottom: 24px;
}

.hd-chart-svg {
  display: block;
}

.hd-grid-h {
  stroke: #f1f5f9;
  stroke-width: 1;
  stroke-dasharray: 4 3;
}

.hd-bar {
  transition: opacity 0.15s;
}

.hd-bar:hover { opacity: 0.95; fill: url(#barGradHov); }

.hd-rev-dot { cursor: pointer; }

.hd-chart-xlabels {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 24px;
}

.hd-chart-xlabel {
  position: absolute;
  bottom: 0;
  width: 56px;
  text-align: center;
  font-size: 0.63rem;
  color: #94a3b8;
  font-weight: 600;
  letter-spacing: 0.02em;
}

/* ── Debug ──────────────────────────────────────── */
.hd-debug {
  margin-top: 20px;
  font-size: 0.78rem;
  color: #94a3b8;
}

.hd-debug__summary {
  cursor: pointer;
  user-select: none;
  padding: 6px 0;
}

.hd-debug__code {
  display: block;
  margin-top: 6px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.75rem;
  word-break: break-all;
  color: #475569;
}

/* ── Responsive tweaks ──────────────────────────── */
@media (max-width: 640px) {
  .hd-main { padding: 14px 14px 32px; }
  .hd-kpi__value { font-size: 1.4rem; }
  .hd-finance__balance-val { font-size: 1.5rem; }
  .hd-panel { padding: 16px 14px; }
}

@media (max-width: 400px) {
  .hd-kpi { flex-direction: column; gap: 8px; padding: 14px; }
  .hd-kpi__value { font-size: 1.6rem; }
  .hd-bed-legend__row { grid-template-columns: 10px 1fr 60px 22px 32px; }
}

/* ── Reduce motion ──────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .hd-kpi, .hd-space-card { transition: none; }
  .hd-sk { animation: none; background: #e9edf4; }
}
</style>
